import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { API_ciudadanos, BASE_URL } from "../../config/apiConfig";
import Navbar from "../../components/Navbar";
import BuscadorUsuarios from "../../components/dashboard_tecnico/BuscadorUsuarios";
import Filtros from "../../components/dashboard_tecnico/FiltrosExpedientes";
import TarjetaUsuario from "../../components/dashboard_tecnico/TarjetaUsuario";
import LoadingSpinner from "../../components/LoadingSpinner";
import "./css/Dashboard.css";
import { useAuth } from "../../context/AuthContext";
import ImageWithAuth from "../../components/ciudadano/ImageWithAuth";
import { FiDownload } from "react-icons/fi";

function filenameFromDisposition(header, fallback) {
  if (!header) return fallback;
  // content-disposition: attachment; filename="ciudadanos.xlsx"
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  if (m && m[1]) return decodeURIComponent(m[1]);
  return fallback;
}

const UsuariosAdmin = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [filtros, setFiltros] = useState({ fechaDesde: "", fechaHasta: "" });
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid"); // "grid" o "table"
  // Estado para guardar las ocupaciones y colectivos que vienen de la api
  const [ocupacionesDisponibles, setOcupacionesDisponibles] = useState([]);
  const [colectivosDisponibles, setColectivosDisponibles] = useState([]);

  useEffect(() => {
    const fetchUsuarios = async () => {
      setLoading(true);
      try {
        const res = await fetch(API_ciudadanos, {
          headers: {
            "x-auth-token": token,
          },
        });
        const data = await res.json();

        if (Array.isArray(data)) {
          const estadosValidos = ["activo", "inactivo", "aceptado"];

          // Filtrar por estado del usuario con manejo seguro
          const filtrados = data.filter((user) => {
            // if (!user.estadoUsuario) {
            //   return false;
            // }
            // return estadosValidos.includes(user.estadoUsuario.toLowerCase());
            return true;
          });

          // Adaptar los datos de usuario
          const adaptados = filtrados.map((user) => ({
            id: user.userId,
            foto: user?.profileImageUrl ? `${BASE_URL}${user.profileImageUrl}` : "",
            nombre: user.name,
            apellido: user.lastName,
            dni: user.dni,
            estadoLaboral: user.estadoLaboral,
            estado: user.estadoUsuario,
            birthdate: user.birthdate,
            ocupaciones: user.ocupaciones || [],
            colectivosSociales: user.colectivosSociales || [],
            programas: user.programasInscritos || [],
            updatedAt: user.updatedAt,
          }));

          setUsers(adaptados);

          // Recoger ocupaciones únicas para los filtros
          const todasOcupaciones = data.flatMap((user) => user.ocupaciones || []).filter(Boolean);

          const ocupacionesUnicas = [...new Set(todasOcupaciones)];
          setOcupacionesDisponibles(ocupacionesUnicas);

          // Recoger colectivos únicos para los filtros
          const todosColectivos = data.flatMap((user) => user.colectivosSociales || []).filter(Boolean);

          const colectivosUnicos = [...new Set(todosColectivos)];
          setColectivosDisponibles(colectivosUnicos);
        } else {
          setUsers([]);
          setOcupacionesDisponibles([]);
          setColectivosDisponibles([]);
        }
      } catch (error) {
        console.error("Error al cargar usuarios:", error);
        setUsers([]);
        setOcupacionesDisponibles([]);
        setColectivosDisponibles([]);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchUsuarios();
    }
  }, [token]);

  // Funcion para descargar excel usuarios

  const handleDownloadExcel = async () => {
    try {
      const res = await fetch(`${API_ciudadanos}/export/excel`, {
        headers: {
          "x-auth-token": token,
        },
      });

      if (!res.ok) {
        console.log("Error al intertar descargar el archivo");
        throw new Error("Error al intertar descargar el archivo");
      }

      const blob = await res.blob();
      const cd = res.headers.get("content-disposition");
      const finalName = filenameFromDisposition(cd, "ciudadanos.xlsx");

      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = finalName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);

      console.log("Excel exportado correctamente");
    } catch (error) {
      console.log(error);
    }
  };

  // Calcular la edad con la fecha de nacimiento para filtrar por edad
  const calcularEdad = (fechaNacimiento) => {
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    return edad;
  };

  // Filtrar por edad
  const filtrarPorEdad = (user) => {
    // Si el usuario no tiene fecha de nacimiento, incluirlo igualmente
    if (!user.birthdate) return true;

    const edad = calcularEdad(user.birthdate);
    const edadMin = filtros.minAge ?? 14;
    const edadMax = filtros.maxAge ?? 70;
    return edad >= edadMin && edad <= edadMax;
  };

  // Filtrar por Situación labora (Desempleado / Ocupado)
  const filtrarPorSituacionLaboral = (user) => {
    if (!filtros.situacionLaboral || filtros.situacionLaboral.length === 0) return true;
    return filtros.situacionLaboral.includes(user.estadoLaboral?.toLowerCase());
  };

  // Filtrar por ocupaciones
  const filtrarPorOcupaciones = (user) => {
    if (!filtros.ocupaciones || filtros.ocupaciones.length === 0) return true;
    return user.ocupaciones?.some((ocup) => filtros.ocupaciones.includes(ocup));
  };

  // Filtrar por colectivos
  const filtrarPorColectivos = (user) => {
    if (!filtros.colectivos || filtros.colectivos.length === 0) return true;
    return user.colectivosSociales?.some((c) => filtros.colectivos.includes(c));
  };

  // Filtrar por Programas en los que están inscritos
  const filtrarPorProgramas = (user) => {
    if (!filtros.programas || filtros.programas.length === 0) return true;
    return user.programas?.some((p) => filtros.programas.includes(p));
  };

  // Filtrar por usuarios activos, inactivos o aceptados
  const filtrarPorEstadoUsuario = (user) => {
    if (!filtros.estadoUsuario || filtros.estadoUsuario === "") return true;
    return user.estado === filtros.estadoUsuario;
  };

  // Filtrar por fecha de registro
  const filtrarPorFechaRegistro = (user) => {
    if (!filtros.fechaInicio || !filtros.fechaFin) return true;

    const fechaUsuario = new Date(user.updatedAt);
    fechaUsuario.setHours(0, 0, 0, 0); // quitar horas

    const desde = new Date(filtros.fechaInicio);
    const hasta = new Date(filtros.fechaFin);
    desde.setHours(0, 0, 0, 0);
    hasta.setHours(0, 0, 0, 0);

    return fechaUsuario >= desde && fechaUsuario <= hasta;
  };

  // Mostrar usuarios teniendo en cuenta los diferentes filtros
  const usuariosFiltrados = users.filter((user) => {
    return (
      `${user.nombre} ${user.apellido} ${user.dni}`.toLowerCase().includes(search.toLowerCase()) &&
      filtrarPorEdad(user) &&
      filtrarPorOcupaciones(user) &&
      filtrarPorProgramas(user) &&
      filtrarPorSituacionLaboral(user) &&
      filtrarPorEstadoUsuario(user) &&
      filtrarPorFechaRegistro(user) &&
      filtrarPorColectivos(user)
    );
  });

  // Ordenar los usuarios filtrados por apellido o fecha de registro si está activo
  const filteredUsers = [...usuariosFiltrados].sort((a, b) => {
    if (filtros.orden === "alfabetico") {
      return (a.apellido || "").localeCompare(b.apellido || "", "es", {
        sensitivity: "base",
      });
    } else if (filtros.orden === "registro") {
      return new Date(b.updatedAt) - new Date(a.updatedAt);
    }
    return 0;
  });

  //Called by the child once the server confirms the deletion
  const handleUserDeleted = (id) => {
    // console.log("Usuario eliminado:", id);
    setUsers((prev) => prev.filter((u) => u.id !== id)); // optimistic local removal
  };
  // Componente para renderizar la tabla de usuarios
  const TablaUsuarios = () => (
    <div className="tabla-usuarios" style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
        <thead>
          <tr
            style={{
              backgroundColor: "#f5f5f5",
              borderBottom: "2px solid #ddd",
            }}
          >
            <th
              style={{
                padding: "12px 8px",
                textAlign: "left",
                borderRight: "1px solid #ddd",
              }}
            >
              Foto
            </th>
            <th
              style={{
                padding: "12px 8px",
                textAlign: "left",
                borderRight: "1px solid #ddd",
              }}
            >
              Nombre Completo
            </th>
            <th
              style={{
                padding: "12px 8px",
                textAlign: "left",
                borderRight: "1px solid #ddd",
              }}
            >
              DNI
            </th>
            <th
              style={{
                padding: "12px 8px",
                textAlign: "left",
                borderRight: "1px solid #ddd",
              }}
            >
              Edad
            </th>
            <th
              style={{
                padding: "12px 8px",
                textAlign: "left",
                borderRight: "1px solid #ddd",
              }}
            >
              Estado Laboral
            </th>
            <th
              style={{
                padding: "12px 8px",
                textAlign: "left",
                borderRight: "1px solid #ddd",
              }}
            >
              Estado Usuario
            </th>
            <th
              style={{
                padding: "12px 8px",
                textAlign: "left",
                borderRight: "1px solid #ddd",
              }}
            >
              Ocupaciones
            </th>
            <th
              style={{
                padding: "12px 8px",
                textAlign: "left",
                borderRight: "1px solid #ddd",
              }}
            >
              Colectivos
            </th>
            <th
              style={{
                padding: "12px 8px",
                textAlign: "left",
                borderRight: "1px solid #ddd",
              }}
            >
              Programas
            </th>
            <th style={{ padding: "12px 8px", textAlign: "left" }}>Fecha Registro</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map((user, index) => (
            <tr
              key={user.id}
              style={{
                borderBottom: "1px solid #eee",
                backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
              }}
            >
              <td style={{ padding: "8px", borderRight: "1px solid #eee" }}>
                <ImageWithAuth
                  path={user.foto} // this is your protected profileImageUrl
                  token={token}
                  alt={`${user.nombre} ${user.apellido}`}
                  fallback="/avatars/default-u.jpg"
                  className=""
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                  // header="Authorization"  // uncomment if your API expects Bearer instead of x-auth-token
                />
              </td>
              <td
                style={{
                  padding: "8px",
                  borderRight: "1px solid #eee",
                  fontWeight: "500",
                }}
              >
                <Link to={`/admin/usuario/${user.id}`} style={{ textDecoration: "none", color: "#333" }}>
                  {user.nombre} {user.apellido}
                </Link>
              </td>
              <td style={{ padding: "8px", borderRight: "1px solid #eee" }}>{user.dni}</td>
              <td style={{ padding: "8px", borderRight: "1px solid #eee" }}>{user.birthdate ? calcularEdad(user.birthdate) : "-"}</td>
              <td style={{ padding: "8px", borderRight: "1px solid #eee" }}>
                <span
                  style={{
                    padding: "4px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    backgroundColor: user.estadoLaboral === "Desempleado" ? "#ffebee" : "#e8f5e8",
                    color: user.estadoLaboral === "Desempleado" ? "#c62828" : "#2e7d32",
                  }}
                >
                  {user.estadoLaboral || "-"}
                </span>
              </td>
              <td style={{ padding: "8px", borderRight: "1px solid #eee" }}>
                <span
                  style={{
                    padding: "4px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    backgroundColor: user.estado === "activo" ? "#e8f5e8" : user.estado === "inactivo" ? "#ffebee" : "#fff3e0",
                    color: user.estado === "activo" ? "#2e7d32" : user.estado === "inactivo" ? "#c62828" : "#f57c00",
                  }}
                >
                  {user.estado}
                </span>
              </td>
              <td
                style={{
                  padding: "8px",
                  borderRight: "1px solid #eee",
                  maxWidth: "200px",
                }}
              >
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "12px",
                  }}
                >
                  {user.ocupaciones?.join(", ") || "-"}
                </div>
              </td>
              <td
                style={{
                  padding: "8px",
                  borderRight: "1px solid #eee",
                  maxWidth: "150px",
                }}
              >
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "12px",
                  }}
                >
                  {user.colectivosSociales?.join(", ") || "-"}
                </div>
              </td>
              <td
                style={{
                  padding: "8px",
                  borderRight: "1px solid #eee",
                  maxWidth: "200px",
                }}
              >
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "12px",
                  }}
                >
                  {user.programas?.join(", ") || "-"}
                </div>
              </td>
              <td style={{ padding: "8px", fontSize: "12px" }}>
                {user.updatedAt ? new Date(user.updatedAt).toLocaleDateString("es-ES") : "-"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <style>
        {`
          .selector-vista:hover {
            background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e") !important;
          }
        `}
      </style>
      <Navbar />

      <main className="main dashboard-admin">
        <section className="portada between w-100 mb-100">
          {/* Listado de expedientes con buscador, filtros y botón de añadir */}

          <div className="expedientes column">
            <div className="between">
              {/* Buscador y botón añadir usuario */}
              <div className="flex gap-30">
                <BuscadorUsuarios search={search} setSearch={setSearch} />
                <Link to="/registro" className="boton verde bold w-150">
                  Añadir usuario
                </Link>
              </div>

              <div className="flex items-center gap-20">
                {/* Selector de vista */}
                <div className="download-button-wrapper">
                  <button class="download-button flex gap-2" onClick={handleDownloadExcel}>
                    <FiDownload size={20} />
                    <span>Descargar</span>
                  </button>
                </div>
                <select
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                  className="boton verde selector-vista"
                  style={{
                    minWidth: "120px",
                    cursor: "pointer",
                    outline: "none",
                    paddingRight: "35px",
                    backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23007932' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 12px center",
                    backgroundSize: "16px 16px",
                    appearance: "none",
                    WebkitAppearance: "none",
                    MozAppearance: "none",
                  }}
                >
                  <option value="grid">Tarjetas</option>
                  <option value="table">Tabla</option>
                </select>

                <Filtros
                  filtros={filtros}
                  setFiltros={setFiltros}
                  ocupacionesDisponibles={ocupacionesDisponibles}
                  colectivosDisponibles={colectivosDisponibles}
                />
              </div>
            </div>

            <div className={`listado-expedientes ${viewMode === "grid" ? "wrap" : ""} scrollable`}>
              {loading ? (
                <LoadingSpinner message="Cargando usuarios..." />
              ) : filteredUsers.length > 0 ? (
                viewMode === "grid" ? (
                  filteredUsers.map((user) => <TarjetaUsuario key={user.id} user={user} token={token} onDeleted={handleUserDeleted} />)
                ) : (
                  <TablaUsuarios />
                )
              ) : (
                <p className="ml-10">No se encontraron usuarios con estas características.</p>
              )}
            </div>
          </div>
        </section>

        {/* <ListadoTareas /> */}
      </main>
    </>
  );
};

export default UsuariosAdmin;
